<script setup lang="ts">
// Stack of transient notifications, mounted once in AppShell. Reads from the
// toasts store and renders each as a Reka ToastRoot inside a ToastProvider +
// ToastViewport - so we inherit the standard toast behaviour: per-toast
// auto-dismiss that PAUSES on hover/focus, swipe-to-dismiss, an aria-live region
// (assertive for error/warn, polite for info/success) and F8-to-focus. The card
// styling + the kind -> Phosphor icon map stay ours.

import AppIcon from '@/components/AppIcon.vue';
import {
  ToastProvider, ToastRoot, ToastDescription, ToastClose, ToastViewport,
} from '@hq/chat-ui';

import { useToastStore, type ToastKind } from '@/stores/toasts';
import type { IconName } from '@/lib/icons';
import { useI18n } from '@/lib/i18n';

const { t } = useI18n();
const toasts = useToastStore();

const ICONS: Record<ToastKind, IconName> = {
  info:    'info',
  success: 'check-circle',
  warn:    'warning',
  error:   'warning-circle',
};

// A sticky toast (store duration 0) maps to a day - effectively "until the close
// button". `Infinity` is a trap here: a setTimeout(Infinity) fires immediately.
const STICKY_MS = 86_400_000;
function durationFor(ms: number): number {
  return ms > 0 ? ms : STICKY_MS;
}

// Reka emits update:open(false) once on timeout / close-button / swipe. Defer the
// store removal past the leave animation so the slide-out is visible (the node
// stays mounted, data-state="closed", until we drop it from the list).
function onOpenChange(id: number, open: boolean): void {
  if (!open) window.setTimeout(() => toasts.dismiss(id), 200);
}
</script>

<template>
  <ToastProvider :duration="4000" swipe-direction="right" :label="t('toast.region_label')">
    <ToastRoot
      v-for="item in toasts.list"
      :key="item.id"
      :duration="durationFor(item.duration)"
      :type="item.kind === 'error' || item.kind === 'warn' ? 'foreground' : 'background'"
      :class="['toast', `toast--${item.kind}`]"
      @update:open="onOpenChange(item.id, $event)"
    >
      <AppIcon :name="ICONS[item.kind]" :size="16" class="toast__icon" />
      <ToastDescription class="toast__text">{{ item.text }}</ToastDescription>
      <ToastClose class="toast__close" :aria-label="t('common.dismiss')">
        <AppIcon name="x" :size="14" />
      </ToastClose>
    </ToastRoot>
    <ToastViewport class="stack" />
  </ToastProvider>
</template>

<!-- Unscoped: ToastRoot renders into the portalled ToastViewport, so a scoped
     hash would never reach the cards. Classes are `toast`/`stack`-prefixed. -->
<style>
.stack {
  position: fixed;
  bottom: 16px;
  right:  16px;
  z-index: 250;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: min(420px, 92vw);
  max-width: 92vw;
  margin: 0;
  padding: 0;
  list-style: none;
  outline: none;
  pointer-events: none;
}

.toast {
  pointer-events: auto;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: start;
  gap: 10px;
  background: var(--surface-panel);
  border: 1px solid var(--border-subtle);
  border-left-width: 3px;
  border-radius: var(--radius-chip);
  padding: 10px 12px;
  box-shadow: var(--shadow-lg);
  font-size: 13px;
  line-height: 1.5;
}

.toast__icon              { margin-top: 2px; }
.toast--info              { border-left-color: var(--accent-default); }
.toast--info  .toast__icon{ color: var(--accent-default); }
.toast--success           { border-left-color: var(--status-success); }
.toast--success .toast__icon { color: var(--status-success-text); }
.toast--warn              { border-left-color: var(--status-warn); }
.toast--warn  .toast__icon{ color: var(--status-warn-text); }
.toast--error             { border-left-color: var(--status-danger); }
.toast--error .toast__icon{ color: var(--status-danger-text); }

.toast__text {
  margin: 0;
  color: var(--ink-primary);
  word-wrap: break-word;
}

.toast__close {
  background: none;
  border: 0;
  color: var(--ink-subtle);
  cursor: pointer;
  padding: 2px;
  border-radius: var(--radius-chip);
  line-height: 0;
}
.toast__close:hover { background: var(--surface-sunken); color: var(--ink-primary); }

/* Reka drives mount/unmount with data-state (open/closed) + data-swipe; animate
   the enter/leave + swipe off those, replacing the old <TransitionGroup>. */
.toast[data-state="open"]   { animation: toast-in 180ms ease; }
.toast[data-state="closed"] { animation: toast-out 180ms ease forwards; }
.toast[data-swipe="move"]   { transform: translateX(var(--reka-toast-swipe-move-x, 0)); }
.toast[data-swipe="cancel"] { transform: translateX(0); transition: transform 180ms ease; }
.toast[data-swipe="end"]    { animation: toast-out 180ms ease forwards; }

@keyframes toast-in  { from { opacity: 0; transform: translateX(12px); } }
@keyframes toast-out { to   { opacity: 0; transform: translateX(12px); } }
@media (prefers-reduced-motion: reduce) {
  .toast[data-state] { animation: none; }
}
</style>
